import * as React from 'react';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Divider from 'antd/lib/divider';
import Collapse from 'antd/lib/collapse';
import Text from 'antd/lib/typography/Text';
import { AppComponentProps, AppRouteComponentMatchParams } from '@/typescriptHelpers';
import { CaretRightOutlined } from '@ant-design/icons';
import GoToReleaseCTA from './GoToReleaseCTA';
import { routeForReleaseStepOrNav, createReleaseOverviewRoute } from '@/helpers/routeHelpers';
import { ReleaseStepType, ReleaseNav } from '@/types';
import { useParams } from 'react-router-dom';
import * as Storage from '../../helpers/localStorageHelper';
import Flex from 'antd/lib/flex';

export interface AppHomeCardProps extends AppComponentProps {
    displayableVersion: string;
    releaseName?: string;
    backgroundColor?: string;
    borderColor?: string;
    headerComponent: React.ReactNode;
    contentComponent: React.ReactNode;
    sideComponent: React.ReactNode;
    id: string; // release summary id
    releaseId: string;
    shouldStartCollapsed?: boolean;
}

export const AppHomeCard: React.FC<AppHomeCardProps> = ({
    backgroundColor = runwayColors.white,
    borderColor = antGrey.grey4,
    headerComponent,
    sideComponent,
    contentComponent,
    displayableVersion,
    releaseName,
    id,
    releaseId,
    shouldStartCollapsed,
}) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();

    const lastSelectedReleaseStepOrNavForRelease: ReleaseStepType | ReleaseNav =
        Storage.getLastSelectedReleaseStepForRelease(id);

    let releasePath;

    if (lastSelectedReleaseStepOrNavForRelease) {
        releasePath = routeForReleaseStepOrNav(
            orgIdParam,
            appIdParam,
            releaseId,
            lastSelectedReleaseStepOrNavForRelease,
        );
    } else {
        // default to overview for upcoming releases
        releasePath = createReleaseOverviewRoute(orgIdParam, appIdParam, releaseId);
    }

    let versionAndMaybeReleaseName = displayableVersion;
    if (!!releaseName && releaseName != '') {
        versionAndMaybeReleaseName += ' - "' + releaseName + '"';
    }

    return (
        <Collapse
            defaultActiveKey={shouldStartCollapsed ? [] : ['1']}
            ghost
            style={{
                maxWidth: 1600,
                minWidth: 1000,
                borderWidth: 1,
                borderStyle: 'solid',
                borderRadius: 16,
                marginBottom: 24,
                padding: 0,
                backgroundColor: backgroundColor,
                borderColor: borderColor,
            }}
            expandIcon={({ isActive }) => (
                <CaretRightOutlined
                    style={{
                        color: antGrey.grey6,
                        fontSize: 24,
                    }}
                    rotate={isActive ? 90 : 0}
                />
            )}
        >
            <Collapse.Panel
                header={
                    <Flex
                        align="center"
                        justify="space-between"
                        style={{
                            width: '100%',
                        }}
                    >
                        <Flex
                            align="center"
                            justify="flex-start"
                            style={{
                                gap: 17,
                            }}
                        >
                            <Text style={{ fontSize: 16, color: antGrey.grey8 }}>{versionAndMaybeReleaseName}</Text>
                            {headerComponent}
                        </Flex>
                        <GoToReleaseCTA releasePath={releasePath} />
                    </Flex>
                }
                key={'1'}
                style={{
                    padding: 10,
                }}
            >
                <Divider style={{ width: '100%', margin: 0, marginBottom: 20 }} />
                <Flex
                    align="stretch"
                    justify="space-between"
                    style={{
                        height: '100%',
                        width: '100%',
                        gap: 26,
                    }}
                >
                    <Flex
                        vertical
                        align="stretch"
                        justify="flex-start"
                        style={{
                            height: '100%',
                            width: 280,
                            flexShrink: 0,
                        }}
                    >
                        {sideComponent}
                    </Flex>
                    <Flex
                        vertical
                        align="stretch"
                        justify="flex-start"
                        style={{
                            height: '100%',
                            flex: 1,
                            width: '80%',
                        }}
                    >
                        {contentComponent}
                    </Flex>
                </Flex>
            </Collapse.Panel>
        </Collapse>
    );
};
